import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { OrderSummary } from "@/components/OrderSummary";
import { CheckCircle2, Loader2, ArrowRight, AlertCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed | expired
  const [receipt, setReceipt] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let cancelled = false;
    let timer;
    attempts.current = 0;
    setStatus("polling"); setReceipt(null);
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (["paid", "not_required"].includes(data.payment_status)) { setReceipt(data); setStatus("paid"); return; }
        if (["expired", "failed", "refunded"].includes(data.payment_status)) { setStatus(data.payment_status); return; }
        if (attempts.current++ < 10) timer = setTimeout(poll, 2000);
        else setStatus("timeout");
      } catch (e) {
        if (cancelled) return;
        if (attempts.current++ < 10) timer = setTimeout(poll, 2000);
        else setStatus("failed");
      }
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [sessionId]);

  return (
    <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="payment-success">
      {status === "polling" && (
        <>
          <Loader2 className="w-10 h-10 mx-auto text-[#FF9F1C] animate-spin" />
          <h1 className="mt-6 font-display font-black text-3xl">Finishing up…</h1>
          <p className="mt-3 text-[#7A7D8C]">Confirming your registration.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <div className="w-16 h-16 rounded-full bg-[#2EC4B6]/15 text-[#0F7A72] grid place-items-center mx-auto"><CheckCircle2 className="w-8 h-8" /></div>
          <h1 className="mt-6 font-display font-black text-4xl">You're in!</h1>
          <p className="mt-4 text-[#7A7D8C]">Registration confirmed. We'll email you the event details soon.</p>
          {receipt?.order_summary && <div className="mt-6"><OrderSummary summary={receipt.order_summary} prefix="confirmation-order" paid /></div>}
          {!receipt?.order_summary && receipt?.amount != null && <p className="mt-6 font-semibold" data-testid="confirmation-legacy-total">Total paid: ${Number(receipt.amount).toFixed(2)}</p>}
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/" data-testid="confirmation-home" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF9F1C] text-white font-bold shadow-[0_12px_30px_rgb(255,159,28,0.35)]">Back home <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/parent/signup" data-testid="confirmation-create-account" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-200 font-bold hover:border-[#FF9F1C]">Create parent account</Link>
          </div>
        </>
      )}
      {status === "refunded" && <div data-testid="confirmation-refunded"><h1 className="font-display font-black text-3xl">Payment refunded</h1><p className="mt-4 text-[#7A7D8C]">This registration payment has been refunded.</p></div>}
      {(status === "failed" || status === "expired" || status === "timeout") && (
        <>
          <div className="w-16 h-16 rounded-full bg-red-50 text-[#E71D36] grid place-items-center mx-auto"><AlertCircle className="w-8 h-8" /></div>
          <h1 className="mt-6 font-display font-black text-3xl">We couldn't confirm the payment</h1>
          <p className="mt-4 text-[#7A7D8C]">{status === "expired" ? "The checkout session expired." : "If Stripe charged you, we'll reconcile automatically — try refreshing in a minute or contact us."}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/register" data-testid="confirmation-retry" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF9F1C] text-white font-bold">Try again</Link>
            <Link to="/contact" data-testid="confirmation-contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-200 font-bold">Contact us</Link>
          </div>
        </>
      )}
    </div>
  );
}
